import React, { useState } from "react";
import { useApp } from "@/context/AppContext";
import api, { apiError } from "@/lib/api";
import { PageHeader } from "@/components/common/Common";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { toast } from "sonner";

export default function ProfilePage() {
  const { t, user, loadMe } = useApp();
  const [form, setForm] = useState({ first_name: user.first_name, last_name: user.last_name, phone: user.phone || "" });
  const [pw, setPw] = useState({ current_password: "", new_password: "", confirm: "" });
  const [saving, setSaving] = useState(false);

  const saveProfile = async () => {
    setSaving(true);
    try { await api.put("/profile", form); await loadMe(); toast.success("Profile updated"); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };
  const changePw = async () => {
    if (pw.new_password !== pw.confirm) return toast.error("Passwords do not match.");
    setSaving(true);
    try { await api.post("/profile/change-password", { current_password: pw.current_password, new_password: pw.new_password }); toast.success("Password changed"); setPw({ current_password: "", new_password: "", confirm: "" }); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  return (
    <div>
      <PageHeader title={t("myProfile")} subtitle={user.email} />
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="bg-card border border-border rounded-md p-6 text-center">
          <div className="h-20 w-20 rounded-full bg-primary text-white flex items-center justify-center text-2xl font-bold font-heading mx-auto">{user.first_name[0]}{user.last_name[0]}</div>
          <h3 className="font-heading font-bold text-lg mt-3">{user.first_name} {user.last_name}</h3>
          <p className="text-sm text-muted-foreground">@{user.username}</p>
          <div className="flex flex-wrap gap-1 justify-center mt-3">{user.role_names?.map((r) => <Badge key={r} variant="secondary">{r}</Badge>)}</div>
          <div className="mt-4 text-sm text-left space-y-2 border-t border-border pt-4">
            <div className="flex justify-between"><span className="text-muted-foreground">{t("branch")}</span><span className="text-right">{user.branches?.map((b) => b.name).join(", ") || "—"}</span></div>
            <div className="flex justify-between"><span className="text-muted-foreground">{t("accountStatus")}</span><Badge variant="secondary" className="bg-[hsl(var(--success))]/10 text-[hsl(var(--success))]">{user.status}</Badge></div>
            <div className="flex justify-between"><span className="text-muted-foreground">{t("lastLogin")}</span><span>{user.last_login ? new Date(user.last_login).toLocaleString() : "—"}</span></div>
          </div>
        </div>

        <div className="lg:col-span-2">
          <Tabs defaultValue="details">
            <TabsList>
              <TabsTrigger value="details" data-testid="profile-tab-details">{t("profile")}</TabsTrigger>
              <TabsTrigger value="password" data-testid="profile-tab-password">{t("changePassword")}</TabsTrigger>
            </TabsList>
            <TabsContent value="details">
              <div className="bg-card border border-border rounded-md p-6 space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div><Label>{t("firstName")}</Label><Input value={form.first_name} onChange={(e) => setForm({ ...form, first_name: e.target.value })} className="mt-1.5" data-testid="profile-firstname" /></div>
                  <div><Label>{t("lastName")}</Label><Input value={form.last_name} onChange={(e) => setForm({ ...form, last_name: e.target.value })} className="mt-1.5" data-testid="profile-lastname" /></div>
                </div>
                <div><Label>{t("phone")}</Label><Input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} className="mt-1.5" data-testid="profile-phone" /></div>
                <Button onClick={saveProfile} disabled={saving} data-testid="profile-save-btn">{t("save")}</Button>
              </div>
            </TabsContent>
            <TabsContent value="password">
              <div className="bg-card border border-border rounded-md p-6 space-y-4 max-w-md">
                <div><Label>{t("currentPassword")}</Label><Input type="password" value={pw.current_password} onChange={(e) => setPw({ ...pw, current_password: e.target.value })} className="mt-1.5" data-testid="profile-current-pw" /></div>
                <div><Label>{t("newPassword")}</Label><Input type="password" value={pw.new_password} onChange={(e) => setPw({ ...pw, new_password: e.target.value })} className="mt-1.5" data-testid="profile-new-pw" /></div>
                <div><Label>{t("confirmPassword")}</Label><Input type="password" value={pw.confirm} onChange={(e) => setPw({ ...pw, confirm: e.target.value })} className="mt-1.5" data-testid="profile-confirm-pw" /></div>
                <Button onClick={changePw} disabled={saving || !pw.current_password || !pw.new_password} data-testid="profile-change-pw-btn">{t("changePassword")}</Button>
              </div>
            </TabsContent>
          </Tabs>
        </div>
      </div>
    </div>
  );
}
